/**
 * Build step 7 — checklists and routines (ADR-001 §11.9, §7.3, §9; operator decision: a member sets up their
 * own client's checklist with no approval). See checklists.ts for the rules.
 *
 * Visibility follows the client: managers all; an ops_member the clients they are the current SPOC of;
 * requesters and HR none. Out-of-scope ids are 404, never 403. Every write is audited (real actor +
 * acting-as); viewing-as is read-only. Additions beyond the §11.9 table, all for screens the prototype has:
 * `GET /checklists` (the Checklists screen across clients), template item edits, and ticking a routine copy
 * (`/routine-instances/{id}/done`, the same proof rule as a checklist run). The bulk sheet is sent as rows the
 * SPA read from the file (like targets import) rather than as a signed upload id.
 */
import { z } from 'zod';
import { IdempotencyHeaders, IfMatchHeaders } from './common.js';
import { AddChecklistItemsBody, AddTemplateItemsBody, ApplyTemplateBody, ApplyTemplateResponse, BulkRoutinesBody, BulkRoutinesResponse, ChecklistItemParams, ChecklistRun, ChecklistTemplate, ChecklistTemplateList, ChecklistsOverview, ChecklistsOverviewQuery, ClientChecklist, ClientChecklistList, ClientRoutine, ClientRoutines, CreateClientChecklistBody, CreateRoutineBody, CreateTemplateBody, RoutineInstance, RoutineParams, TemplateItemParams, TickProofBody, UpdateChecklistItemBody, UpdateClientChecklistBody, UpdateRoutineBody, UpdateTemplateBody, UpdateTemplateItemBody, } from './checklists.js';
import { API_BASE, defineRoute } from './route.js';
const IdOnly = z.object({ id: z.uuid() });
const READ = ['clients.read.all', 'clients.read.own'];
const WRITE = ['checklist.write.any', 'checklist.write.own'];
const TEMPLATES = ['checklist.write.any'];
const SCOPE = 'Managers; or the client’s current SPOC. Others: 404.';
const WRITE_SCOPE = 'Managers (any client), or an ops member for a client they are the SPOC of — no approval. Others: 404. Not while viewing as someone (403).';
const TICK_SCOPE = 'When it has a task (not dropped), ticking closes that task and unticking reopens it, so only the task’s assignee or a manager may — anyone else who can see it (e.g. the client’s SPOC when the task is assigned to someone else) gets 403. With no task: the client’s SPOC or a manager. Others: 404. Not while viewing as someone (403).';
const ETAG = { ETag: 'W/"<version>" of the returned resource' };
export const step7Routes = {
    // ============================================================ the Checklists screen
    'checklists.overview': defineRoute({
        id: 'checklists.overview',
        method: 'GET',
        path: `${API_BASE}/checklists`,
        summary: 'The Checklists screen: stats, what is due this week or missed, and progress by client',
        access: { auth: 'session', anyOf: READ, note: 'Managers → all clients; ops_member → own clients. Others: 403.' },
        query: ChecklistsOverviewQuery,
        success: { status: 200, description: 'Overview for this month', schema: ChecklistsOverview },
        errors: ['no_session', 'forbidden', 'validation_failed'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    // ============================================================ templates (managers)
    'checklistTemplates.list': defineRoute({
        id: 'checklistTemplates.list',
        method: 'GET',
        path: `${API_BASE}/checklist-templates`,
        summary: 'Checklist templates with their lines (the Templates tab)',
        description: 'Active first, then by segment. Members read them too (to pick one when setting up their own client).',
        access: { auth: 'session', anyOf: WRITE, note: 'Managers and ops members. Others: 403.' },
        success: { status: 200, description: 'Templates', schema: ChecklistTemplateList },
        errors: ['no_session', 'forbidden'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    'checklistTemplates.create': defineRoute({
        id: 'checklistTemplates.create',
        method: 'POST',
        path: `${API_BASE}/checklist-templates`,
        summary: 'Add a template (the SPA turns "one activity per line, a comma and the due day" into items)',
        description: '409 when an active template already exists for that segment.',
        access: { auth: 'session', anyOf: TEMPLATES, note: 'Founder and associate director. Others: 403.' },
        headers: IdempotencyHeaders,
        body: CreateTemplateBody,
        success: { status: 201, description: 'Created', schema: ChecklistTemplate, headers: ETAG },
        errors: ['no_session', 'forbidden', 'validation_failed', 'conflict'],
        idempotency: 'key',
        rateLimit: 'write',
    }),
    'checklistTemplates.update': defineRoute({
        id: 'checklistTemplates.update',
        method: 'PATCH',
        path: `${API_BASE}/checklist-templates/{id}`,
        summary: 'Rename a template, change its segment, or switch it off',
        description: 'Never changes checklists already made from it.',
        access: { auth: 'session', anyOf: TEMPLATES, deny: 'forbidden', note: 'Founder and associate director. Others: 403.' },
        params: IdOnly,
        headers: IfMatchHeaders,
        body: UpdateTemplateBody,
        success: { status: 200, description: 'Updated', schema: ChecklistTemplate, headers: ETAG },
        errors: ['no_session', 'forbidden', 'not_found', 'validation_failed', 'precondition_failed', 'conflict'],
        idempotency: 'safe',
        concurrency: 'if-match',
        rateLimit: 'write',
    }),
    'checklistTemplates.addItems': defineRoute({
        id: 'checklistTemplates.addItems',
        method: 'POST',
        path: `${API_BASE}/checklist-templates/{id}/items`,
        summary: 'Append lines to a template',
        access: { auth: 'session', anyOf: TEMPLATES, deny: 'forbidden', note: 'Founder and associate director. Others: 403.' },
        params: IdOnly,
        headers: IdempotencyHeaders,
        body: AddTemplateItemsBody,
        success: { status: 201, description: 'The template with the new lines', schema: ChecklistTemplate, headers: ETAG },
        errors: ['no_session', 'forbidden', 'not_found', 'validation_failed'],
        idempotency: 'key',
        rateLimit: 'write',
    }),
    'checklistTemplates.updateItem': defineRoute({
        id: 'checklistTemplates.updateItem',
        method: 'PATCH',
        path: `${API_BASE}/checklist-templates/{id}/items/{iid}`,
        summary: 'Fix a template line or switch it off (If-Match is the template version)',
        access: { auth: 'session', anyOf: TEMPLATES, deny: 'forbidden', note: 'Founder and associate director. Others: 403.' },
        params: TemplateItemParams,
        headers: IfMatchHeaders,
        body: UpdateTemplateItemBody,
        success: { status: 200, description: 'The template', schema: ChecklistTemplate, headers: ETAG },
        errors: ['no_session', 'forbidden', 'not_found', 'validation_failed', 'precondition_failed'],
        idempotency: 'safe',
        concurrency: 'if-match',
        rateLimit: 'write',
    }),
    'checklists.applyTemplate': defineRoute({
        id: 'checklists.applyTemplate',
        method: 'POST',
        path: `${API_BASE}/checklists/apply-template`,
        summary: 'Give many clients a checklist copied from one template',
        description: 'All or nothing per client; a client that already has an active checklist from this template is skipped.',
        access: { auth: 'session', anyOf: TEMPLATES, note: 'Founder and associate director. Others: 403.' },
        headers: IdempotencyHeaders,
        body: ApplyTemplateBody,
        success: { status: 200, description: 'Applied and skipped clients', schema: ApplyTemplateResponse },
        errors: ['no_session', 'forbidden', 'not_found', 'validation_failed'],
        idempotency: 'key',
        rateLimit: 'write',
    }),
    // ============================================================ client checklists
    'checklists.listForClient': defineRoute({
        id: 'checklists.listForClient',
        method: 'GET',
        path: `${API_BASE}/clients/{id}/checklists`,
        summary: 'The client’s checklists with this month’s state of every item',
        access: { auth: 'session', anyOf: READ, note: SCOPE },
        params: IdOnly,
        success: { status: 200, description: 'Checklists, active first', schema: ClientChecklistList },
        errors: ['no_session', 'not_found'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    'checklists.create': defineRoute({
        id: 'checklists.create',
        method: 'POST',
        path: `${API_BASE}/clients/{id}/checklists`,
        summary: 'Set up a checklist for the client — from a template, typed in, or both (no approval step)',
        description: 'Items whose due day is already past this month get their first run next month.',
        access: { auth: 'session', anyOf: WRITE, note: WRITE_SCOPE },
        params: IdOnly,
        headers: IdempotencyHeaders,
        body: CreateClientChecklistBody,
        success: { status: 201, description: 'Created', schema: ClientChecklist, headers: ETAG },
        errors: ['no_session', 'forbidden', 'not_found', 'validation_failed'],
        idempotency: 'key',
        rateLimit: 'write',
    }),
    'checklists.update': defineRoute({
        id: 'checklists.update',
        method: 'PATCH',
        path: `${API_BASE}/client-checklists/{id}`,
        summary: 'Rename a client checklist or switch it off',
        description: 'Switching off stops new runs; this month’s runs already made stay open.',
        access: { auth: 'session', anyOf: WRITE, note: WRITE_SCOPE },
        params: IdOnly,
        headers: IfMatchHeaders,
        body: UpdateClientChecklistBody,
        success: { status: 200, description: 'Updated', schema: ClientChecklist, headers: ETAG },
        errors: ['no_session', 'forbidden', 'not_found', 'validation_failed', 'precondition_failed'],
        idempotency: 'safe',
        concurrency: 'if-match',
        rateLimit: 'write',
    }),
    'checklists.addItems': defineRoute({
        id: 'checklists.addItems',
        method: 'POST',
        path: `${API_BASE}/client-checklists/{id}/items`,
        summary: 'Add items to a client checklist',
        access: { auth: 'session', anyOf: WRITE, note: WRITE_SCOPE },
        params: IdOnly,
        headers: IdempotencyHeaders,
        body: AddChecklistItemsBody,
        success: { status: 201, description: 'The checklist with the new items', schema: ClientChecklist },
        errors: ['no_session', 'forbidden', 'not_found', 'validation_failed'],
        idempotency: 'key',
        rateLimit: 'write',
    }),
    'checklists.updateItem': defineRoute({
        id: 'checklists.updateItem',
        method: 'PATCH',
        path: `${API_BASE}/client-checklists/{id}/items/{iid}`,
        summary: 'Change an item’s text, due day or priority, or switch it off',
        description: 'A changed due day applies from the next run; a run already made keeps its task.',
        access: { auth: 'session', anyOf: WRITE, note: WRITE_SCOPE },
        params: ChecklistItemParams,
        headers: IfMatchHeaders,
        body: UpdateChecklistItemBody,
        success: { status: 200, description: 'The checklist', schema: ClientChecklist, headers: ETAG },
        errors: ['no_session', 'forbidden', 'not_found', 'validation_failed', 'precondition_failed'],
        idempotency: 'safe',
        concurrency: 'if-match',
        rateLimit: 'write',
    }),
    'checklistRuns.done': defineRoute({
        id: 'checklistRuns.done',
        method: 'POST',
        path: `${API_BASE}/checklist-runs/{id}/done`,
        summary: 'Tick this month’s run of an item, with proof (a note, a file, or both)',
        description: '422 with neither note nor file. Also closes the run’s task with the same proof. Ticking a done run again is a no-op returning it.',
        access: { auth: 'session', anyOf: READ, note: TICK_SCOPE },
        params: IdOnly,
        body: TickProofBody,
        success: { status: 200, description: 'The run', schema: ChecklistRun },
        errors: ['no_session', 'forbidden', 'not_found', 'validation_failed'],
        idempotency: 'safe',
        rateLimit: 'write',
    }),
    'checklistRuns.undo': defineRoute({
        id: 'checklistRuns.undo',
        method: 'DELETE',
        path: `${API_BASE}/checklist-runs/{id}/done`,
        summary: 'Untick a run: clears the tick and the proof (prototype: "Reopened. Proof cleared.")',
        description: 'Reopens the run’s task too. The cleared proof stays in the audit log.',
        access: { auth: 'session', anyOf: READ, note: TICK_SCOPE },
        params: IdOnly,
        success: { status: 200, description: 'The run, not done', schema: ChecklistRun },
        errors: ['no_session', 'forbidden', 'not_found'],
        idempotency: 'safe',
        rateLimit: 'write',
    }),
    // ============================================================ routines
    'routines.list': defineRoute({
        id: 'routines.list',
        method: 'GET',
        path: `${API_BASE}/clients/{id}/routines`,
        summary: 'The client’s recurring tasks with their current copies, and the standard monthly list',
        access: { auth: 'session', anyOf: READ, note: SCOPE },
        params: IdOnly,
        success: { status: 200, description: 'Routines', schema: ClientRoutines },
        errors: ['no_session', 'not_found'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    'routines.create': defineRoute({
        id: 'routines.create',
        method: 'POST',
        path: `${API_BASE}/clients/{id}/routines`,
        summary: 'Add a recurring task for the client’s SPOC',
        description: 'The copy due in the current period is made at once (none for a fortnightly task starting next week). 409 when the client already has the same normalised (what, every).',
        access: { auth: 'session', anyOf: WRITE, note: WRITE_SCOPE },
        params: IdOnly,
        headers: IdempotencyHeaders,
        body: CreateRoutineBody,
        success: { status: 201, description: 'The routine with its current copy', schema: ClientRoutine },
        errors: ['no_session', 'forbidden', 'not_found', 'validation_failed', 'conflict'],
        idempotency: 'key',
        rateLimit: 'write',
    }),
    'routines.update': defineRoute({
        id: 'routines.update',
        method: 'PATCH',
        path: `${API_BASE}/clients/{id}/routines/{rid}`,
        summary: 'Change what, how often or on which day; rebuilds the current copy unless it is done',
        access: { auth: 'session', anyOf: WRITE, note: WRITE_SCOPE },
        params: RoutineParams,
        headers: IfMatchHeaders,
        body: UpdateRoutineBody,
        success: { status: 200, description: 'The routine with its current copy', schema: ClientRoutine, headers: ETAG },
        errors: ['no_session', 'forbidden', 'not_found', 'validation_failed', 'precondition_failed', 'conflict'],
        idempotency: 'safe',
        concurrency: 'if-match',
        rateLimit: 'write',
    }),
    'routines.remove': defineRoute({
        id: 'routines.remove',
        method: 'DELETE',
        path: `${API_BASE}/clients/{id}/routines/{rid}`,
        summary: 'Stop a recurring task ("Removed. It will not repeat.")',
        description: 'Switches it off (kept for history) and removes its current copy and task unless done.',
        access: { auth: 'session', anyOf: WRITE, note: WRITE_SCOPE },
        params: RoutineParams,
        success: { status: 204, description: 'Removed' },
        errors: ['no_session', 'forbidden', 'not_found'],
        idempotency: 'safe',
        rateLimit: 'write',
    }),
    'routines.bulk': defineRoute({
        id: 'routines.bulk',
        method: 'POST',
        path: `${API_BASE}/routines/bulk`,
        summary: '“For many clients”: one recurring task for chosen clients, or rows from a sheet (Task, Frequency, Day, Clients)',
        description: 'Duplicates (same normalised what + every on a client) are skipped, never added twice. A member’s non-own clients ' +
            'are skipped as not_your_client. dry_run (sheet) previews without writing. 200 even when everything was skipped.',
        access: { auth: 'session', anyOf: WRITE, note: 'Managers → any client; ops_member → own clients. Others: 403.' },
        headers: IdempotencyHeaders,
        body: BulkRoutinesBody,
        success: { status: 200, description: 'Added and skipped', schema: BulkRoutinesResponse },
        errors: ['no_session', 'forbidden', 'validation_failed'],
        idempotency: 'key',
        rateLimit: 'write',
    }),
    'routineInstances.done': defineRoute({
        id: 'routineInstances.done',
        method: 'POST',
        path: `${API_BASE}/routine-instances/{id}/done`,
        summary: 'Tick the current copy of a recurring task, with proof',
        description: '422 with neither note nor file. Closes its task with the same proof. A done copy is never regenerated.',
        access: { auth: 'session', anyOf: READ, note: TICK_SCOPE },
        params: IdOnly,
        body: TickProofBody,
        success: { status: 200, description: 'The copy', schema: RoutineInstance },
        errors: ['no_session', 'forbidden', 'not_found', 'validation_failed'],
        idempotency: 'safe',
        rateLimit: 'write',
    }),
    'routineInstances.undo': defineRoute({
        id: 'routineInstances.undo',
        method: 'DELETE',
        path: `${API_BASE}/routine-instances/{id}/done`,
        summary: 'Untick a routine copy: clears the tick and the proof, reopens its task',
        access: { auth: 'session', anyOf: READ, note: TICK_SCOPE },
        params: IdOnly,
        success: { status: 200, description: 'The copy, not done', schema: RoutineInstance },
        errors: ['no_session', 'forbidden', 'not_found'],
        idempotency: 'safe',
        rateLimit: 'write',
    }),
};
//# sourceMappingURL=routes.step7.js.map